// Store de autenticación con Zustand
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { API_URL } from '../lib/api';


export interface AuthUser {
  id: string;
  username: string;
  email?: string;
  avatar?: string;
  isGuest: boolean;
  createdAt: string;
  // Sistema de niveles (solo usuarios registrados)
  level: number;
  xp: number;
  nextLevelXp: number;
  currentLevelXp: number;
  playTimeSeconds: number;
  levelBadgePulsing: boolean; // ephemeral, no persiste
}

export interface XpAwardedPayload {
  xpAwarded: number;
  totalXp: number;
  currentLevel: number;
  nextLevelXp: number;
  currentLevelXp: number;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Acciones
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  loginAsGuest: (username: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  initAuth: () => Promise<void>;
  updateLevel: (data: XpAwardedPayload) => void;
  triggerBadgePulse: () => void;
}

async function fetchLevelData(token: string): Promise<Partial<AuthUser>> {
  try {
    const { data } = await axios.get(`${API_URL}/api/users/me/level`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = data.data;
    return {
      xp: d.xp,
      level: d.currentLevel,
      currentLevelXp: d.currentLevelXp,
      nextLevelXp: d.nextLevelXp,
      playTimeSeconds: d.playTimeSeconds,
    };
  } catch {
    return {};
  }
}

const defaultLevelFields = {
  level: 1,
  xp: 0,
  nextLevelXp: 50,  // totalXpForLevel(1) = 50*1^2.2 = 50
  currentLevelXp: 0, // totalXpForLevel(0) = 0
  playTimeSeconds: 0,
  levelBadgePulsing: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Inicializar desde localStorage + re-hidratar nivel desde servidor
      initAuth: async () => {
        const { token, user } = get();
        if (token && user) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          set({ isAuthenticated: true });

          if (!user.isGuest) {
            const levelData = await fetchLevelData(token);
            set(s => ({ user: s.user ? { ...s.user, ...levelData, levelBadgePulsing: false } : null }));
          }
        }
      },

      // Login con email y contraseña
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
          const { token, user } = data.data;

          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const levelData = user.isGuest ? {} : await fetchLevelData(token);
          set({
            token,
            user: { ...defaultLevelFields, ...user, ...levelData },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err: any) {
          const message = err.response?.data?.error || 'Error al iniciar sesión';
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      // Registro de nuevo usuario
      register: async (username, email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await axios.post(`${API_URL}/api/auth/register`, { username, email, password });
          const { token, user } = data.data;

          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const levelData = await fetchLevelData(token);
          set({
            token,
            user: { ...defaultLevelFields, ...user, ...levelData },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err: any) {
          const message = err.response?.data?.error || 'Error al registrarse';
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      // Login con Google OAuth
      loginWithGoogle: async (credential: string) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await axios.post(`${API_URL}/api/auth/google`, { credential });
          const { token, user } = data.data;

          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const levelData = await fetchLevelData(token);
          set({
            token,
            user: { ...defaultLevelFields, ...user, ...levelData },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err: any) {
          const message = err.response?.data?.error || 'Error al iniciar sesión con Google';
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      // Entrar como invitado (sin niveles)
      loginAsGuest: async (username) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await axios.post(`${API_URL}/api/auth/guest`, { username });
          const { token, user } = data.data;

          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          set({
            token,
            user: { ...defaultLevelFields, ...user },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err: any) {
          const message = err.response?.data?.error || 'Error al entrar como invitado';
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      // Cerrar sesión
      logout: () => {
        delete axios.defaults.headers.common['Authorization'];
        set({ user: null, token: null, isAuthenticated: false, error: null });
      },

      clearError: () => set({ error: null }),

      // Actualizar XP/nivel tras xp:awarded
      updateLevel: (data: XpAwardedPayload) => {
        set(s => ({
          user: s.user ? {
            ...s.user,
            xp: data.totalXp,
            level: data.currentLevel,
            nextLevelXp: data.nextLevelXp,
            currentLevelXp: data.currentLevelXp,
          } : null,
        }));
      },

      // Activar animación del badge de nivel durante 2 segundos
      triggerBadgePulse: () => {
        set(s => ({ user: s.user ? { ...s.user, levelBadgePulsing: true } : null }));
        setTimeout(() => {
          set(s => ({ user: s.user ? { ...s.user, levelBadgePulsing: false } : null }));
        }, 2000);
      },
    }),
    {
      name: 'jugaly-auth',
      partialize: (state) => ({
        token: state.token,
        // levelBadgePulsing excluido intencionalmente (ephemeral)
        user: state.user ? { ...state.user, levelBadgePulsing: false } : null,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
